import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Building2, Loader2, Search, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { fetchClientList, type ClientRow, type ClientScope } from "@/api/clients";

/**
 * D-145 F3-1 — the picker must search the user's WIDEST allowed scope. Without
 * an explicit `scope` the backend defaults to 'my' (ClientListService), which a
 * supervisor never satisfies (owns no accounts) → the picker was ALWAYS empty
 * for supervisors. allowedScopes comes from the same endpoint's envelope; the
 * ranking mirrors the backend vocabulary my ⊂ team ⊂ all.
 */
function widestScope(allowed: ClientScope[]): ClientScope {
  if (allowed.includes("all")) return "all";
  if (allowed.includes("team")) return "team";
  return "my";
}

/**
 * Home/F6 — reusable «no-context create-for-client» resolver (D-031 helper, NOT a
 * form): a light holding-grain client search that resolves the missing holdingId
 * for canonical create drawers opened OUTSIDE a client context (e.g. Home
 * «+ Створити → Зустріч» → pick client → canonical ScheduleMeetingDrawer).
 * Reuses the live /Custom/frontend/clients list API (resolver-scoped server-side).
 */
export function ClientPickerDrawer({
  open,
  onClose,
  onPick,
  title = "Оберіть клієнта",
  hint,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (holdingId: string, holdingName: string) => void;
  title?: string;
  hint?: string;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
      return;
    }
    // focus the search as soon as the drawer mounts.
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  // D-145 F3-1 — one light probe per session for the scope envelope; the list
  // query then always carries the widest allowed scope (supervisor→team,
  // manager→my as before; the backend still clamps to allowedScopes).
  const scopeQ = useQuery({
    queryKey: ["client-picker-scope"],
    queryFn: ({ signal }) => fetchClientList({ limit: 1 }, signal).then((r) => widestScope(r.allowedScopes ?? [])),
    enabled: open,
    staleTime: Infinity,
  });
  const scope = scopeQ.data;

  const listQ = useQuery({
    queryKey: ["client-picker", debounced, scope],
    queryFn: ({ signal }) => fetchClientList({ q: debounced || undefined, limit: 20, scope }, signal),
    enabled: open && scope !== undefined,
    staleTime: 30_000,
  });

  const items: ClientRow[] = useMemo(() => listQ.data?.items ?? [], [listQ.data]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[450px]"
      >
        <div className="sticky top-0 z-10 border-b bg-background px-5 py-4">
          <div className="flex items-center justify-between">
            <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
              <Building2 className="h-4 w-4 text-primary" /> {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Пошук клієнта…"
              className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {listQ.isPending ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Завантаження…
            </div>
          ) : listQ.isError ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              Не вдалося завантажити список. Спробуйте ще раз.
            </p>
          ) : items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              {debounced ? "Нічого не знайдено." : "Почніть вводити назву клієнта."}
            </p>
          ) : (
            <ul>
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onPick(c.id, c.name)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left",
                      "hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{c.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {[c.manager, c.region].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
